import React, { useEffect, useState } from "react";
import { unlockFolder, setFolderLock, removeFolderLock } from "../api";
import { IcoLock, IcoUnlock, IcoKey, IcoEye, IcoEyeOff, IcoX, IcoInfo, IcoArrowLeft } from "./Icons";

export const MIN_PASSWORD_LENGTH = 4;

// Ô nhập mật khẩu có nút hiện/ẩn.
export function PasswordInput({ value, onChange, placeholder, autoFocus, autoComplete = "new-password" }) {
  const [show, setShow] = useState(false);
  return (
    <span className="field-box">
      <IcoKey size={17} />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="mini-btn"
        onClick={() => setShow((s) => !s)}
        title={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        aria-label={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
      >
        {show ? <IcoEyeOff size={16} /> : <IcoEye size={16} />}
      </button>
    </span>
  );
}

/* ── Thư mục có mật khẩu mà chưa mở khóa: hiện thay cho nội dung thư mục ── */
export function LockedFolderPanel({ path, lockPath, ownerName, onUnlocked, onBack }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPassword(""); setError(""); }, [path]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await unlockFolder(path, password);
      if (res.ok) onUnlocked();
      else setError(res.message);
    } catch {
      setError("Không kết nối được tới máy chủ.");
    }
    setBusy(false);
  };

  const lockedName = (lockPath || path).split("/").pop();

  return (
    <form className="locked-panel" onSubmit={submit}>
      <span className="locked-art"><IcoLock size={30} strokeWidth={1.6} /></span>
      <h3>Thư mục có mật khẩu</h3>
      <p>
        Nhập mật khẩu của thư mục <b>{lockedName}</b> để xem bên trong.
        {ownerName ? <> Người đặt: <b>{ownerName}</b>.</> : null}
      </p>

      <div className="locked-form">
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder="Mật khẩu thư mục…"
          autoFocus
          autoComplete="off"
        />
        <button className="btn btn-primary" type="submit" disabled={!password || busy}>
          <IcoUnlock size={17} /> <span>{busy ? "Đang kiểm tra…" : "Mở thư mục"}</span>
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <button type="button" className="btn btn-quiet btn-sm" onClick={onBack}>
        <IcoArrowLeft size={15} /> <span>Quay lại</span>
      </button>
    </form>
  );
}

/* ── Hộp đặt / đổi / gỡ mật khẩu của một thư mục ──
   folder: { path, name, locked }. Chỉ mở được với người có quyền (máy chủ kiểm tra lại). */
export function LockDialog({ folder, userName, onDone, onCancel }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSave = password.length >= MIN_PASSWORD_LENGTH && confirm === password && !busy;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError("");
    const res = await setFolderLock(folder.path, password, userName).catch(() => ({ ok: false, message: "Không kết nối được tới máy chủ." }));
    setBusy(false);
    if (res.ok) onDone(folder.locked ? "Đã đổi mật khẩu thư mục" : "Đã đặt mật khẩu cho thư mục");
    else setError(res.message);
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await removeFolderLock(folder.path).catch(() => ({ ok: false, message: "Không kết nối được tới máy chủ." }));
    setBusy(false);
    if (res.ok) onDone("Đã gỡ mật khẩu, thư mục trở lại công khai");
    else setError(res.message);
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Mật khẩu thư mục">
      <form className="modal-card" onSubmit={save}>
        <div className="modal-head">
          <span className="modal-ico"><IcoLock size={20} /></span>
          <div>
            <h3>{folder.locked ? "Đổi mật khẩu thư mục" : "Đặt mật khẩu thư mục"}</h3>
            <p>Thư mục: <b>{folder.name}</b></p>
          </div>
          <button type="button" className="icon-btn ghost modal-x" onClick={onCancel} aria-label="Đóng">
            <IcoX size={20} />
          </button>
        </div>

        <div className="modal-body">
          <label className="field">
            <span>{folder.locked ? "Mật khẩu mới" : "Mật khẩu"}</span>
            <PasswordInput value={password} onChange={setPassword} placeholder={`Ít nhất ${MIN_PASSWORD_LENGTH} ký tự…`} autoFocus />
          </label>
          <label className="field">
            <span>Nhập lại mật khẩu</span>
            <PasswordInput value={confirm} onChange={setConfirm} placeholder="Nhập lại đúng mật khẩu ở trên…" />
          </label>

          {(tooShort || mismatch || error) && (
            <div className="form-error">
              {error || (tooShort ? `Mật khẩu cần ít nhất ${MIN_PASSWORD_LENGTH} ký tự.` : "Hai ô mật khẩu chưa giống nhau.")}
            </div>
          )}

          <div className="callout info">
            <IcoInfo size={16} />
            <span>
              Người khác phải nhập mật khẩu này mới mở được thư mục và các thư mục con.
              Bạn (người đặt) vào thẳng không cần nhập. Link ảnh đã chép vẫn dùng được
              bình thường trên website và app; riêng trang nào tự đọc danh sách ảnh của
              thư mục này (ví dụ trang Bản tin) sẽ không đọc được nữa.
            </span>
          </div>
        </div>

        <div className="modal-foot">
          {folder.locked && (
            <button type="button" className="btn btn-danger-quiet foot-left" onClick={remove} disabled={busy}>
              <IcoUnlock size={16} /> <span>Gỡ mật khẩu</span>
            </button>
          )}
          <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>Hủy</button>
          <button type="submit" className="btn btn-primary" disabled={!canSave}>
            {busy ? "Đang lưu…" : folder.locked ? "Đổi mật khẩu" : "Đặt mật khẩu"}
          </button>
        </div>
      </form>
    </div>
  );
}
